import { ValidationError } from "./errors.js";
export function singleton(schema) {
    return {
        kind: "singleton",
        schema
    };
}
export function collection(schema) {
    return {
        kind: "collection",
        schema
    };
}
export function defineGraph(entries) {
    return {
        entries,
        validateState(input) {
            return validateState(this, input);
        },
        safeValidateState(input) {
            return safeValidateState(this, input);
        }
    };
}
export function validateState(graph, input) {
    const result = safeValidateState(graph, input);
    if (!result.success) {
        throw result.error;
    }
    return result.data;
}
export function safeValidateState(graph, input) {
    const issues = [];
    if (!isRecord(input)) {
        return failedValidation([
            {
                path: [],
                message: "State must be a plain JSON object."
            }
        ]);
    }
    const parsedState = {};
    const graphKeys = new Set(Object.keys(graph.entries));
    for (const stateKey of Object.keys(input)) {
        if (!graphKeys.has(stateKey)) {
            issues.push({
                path: [stateKey],
                message: `Unknown graph entry "${stateKey}".`
            });
        }
    }
    for (const [entryName, entry] of Object.entries(graph.entries)) {
        if (!Object.hasOwn(input, entryName)) {
            issues.push({
                path: [entryName],
                message: `Missing graph entry "${entryName}".`
            });
            continue;
        }
        const rawValue = input[entryName];
        if (entry.kind === "singleton") {
            const parsed = parseWithSchema(entry.schema, rawValue, [entryName]);
            if (parsed.success) {
                parsedState[entryName] = parsed.data;
            }
            else {
                issues.push(parsed.issue);
            }
            continue;
        }
        if (!isRecord(rawValue)) {
            issues.push({
                path: [entryName],
                message: `Collection "${entryName}" must be a plain JSON object.`
            });
            continue;
        }
        const parsedCollection = {};
        for (const [entityId, entityValue] of Object.entries(rawValue)) {
            const parsed = parseWithSchema(entry.schema, entityValue, [
                entryName,
                entityId
            ]);
            if (parsed.success) {
                parsedCollection[entityId] = parsed.data;
            }
            else {
                issues.push(parsed.issue);
            }
        }
        parsedState[entryName] = parsedCollection;
    }
    if (issues.length > 0) {
        return failedValidation(issues);
    }
    const jsonIssues = [];
    collectJsonCompatibilityIssues(parsedState, [], jsonIssues);
    if (jsonIssues.length > 0) {
        return failedValidation(jsonIssues);
    }
    return {
        success: true,
        data: parsedState
    };
}
function parseWithSchema(schema, input, path) {
    const result = schema.safeParse(input);
    if (result.success) {
        return result;
    }
    return {
        success: false,
        issue: {
            path,
            message: "Value does not match its schema.",
            cause: result.error
        }
    };
}
function failedValidation(issues) {
    return {
        success: false,
        error: new ValidationError("State validation failed.", issues)
    };
}
function isRecord(value) {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        return false;
    }
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function collectJsonCompatibilityIssues(value, path, issues) {
    if (value === null) {
        return;
    }
    const valueType = typeof value;
    if (valueType === "string" || valueType === "boolean") {
        return;
    }
    if (valueType === "number") {
        if (!Number.isFinite(value)) {
            issues.push({
                path,
                message: "JSON-compatible numbers must be finite."
            });
        }
        return;
    }
    if (Array.isArray(value)) {
        for (let index = 0; index < value.length; index += 1) {
            collectJsonCompatibilityIssues(value[index], [...path, index], issues);
        }
        return;
    }
    if (isRecord(value)) {
        for (const [key, childValue] of Object.entries(value)) {
            collectJsonCompatibilityIssues(childValue, [...path, key], issues);
        }
        return;
    }
    issues.push({
        path,
        message: `Unsupported JSON value type "${valueType}".`
    });
}
//# sourceMappingURL=graph.js.map